import type {
  EOrganizationSkillStatus,
  ESkillAccessRole,
  ESkillBundleErrorCode,
  ESkillBundleWarningCode,
  ESkillManifestErrorCode,
  ESkillSourceType,
  TSkillIssueArgs,
} from "./skills";

// NOTE: NSAI-3306/NSAI-3307 — the platform-neutral half of the Skills UI.
//
// Everything here is presentation LOGIC, never presentation: no React, no React
// Native, no DOM. Web and mobile render these shapes very differently but must
// agree on what a bundle issue means, which files count as scripts, whether an
// import may be committed, and what is safe to put in an error or an analytics
// event. Those four answers living in one place is what keeps the two clients
// from drifting into different security behavior.

/* -------------------------------------------------------------------------
 * Resource classification
 * ---------------------------------------------------------------------- */

export enum ESkillResourceKind {
  TEXT = "text",
  ASSET = "asset",
  SCRIPT = "script",
}

// NOTE: Structurally the members both a stored TSkillResource and a preview
// TSkillBundleResourcePreview share, so one classifier serves the detail view
// and the import preview.
export interface ISkillResourceLike {
  skillRelativePath: string;
  contentType: string;
  fileSizeBytes: number;
}

export interface IClassifySkillResourcesProps<
  TResource extends ISkillResourceLike,
> {
  resources: readonly TResource[];
}

export interface IClassifiedSkillResources<
  TResource extends ISkillResourceLike,
> {
  texts: TResource[];
  assets: TResource[];
  scripts: TResource[];
  hasScripts: boolean;
  totalCount: number;
  totalBytes: number;
}

/* -------------------------------------------------------------------------
 * Capabilities
 * ---------------------------------------------------------------------- */

// NOTE: The per-visibility permission answers arrive already resolved, because
// each client reads them from its own permission hook — this side only decides
// how ownership combines with them.
export interface IResolveSkillCapabilitiesProps {
  isPublic: boolean;
  isOwnedByUser: boolean;
  canWriteVisibility: boolean;
  canDeleteVisibility: boolean;
}

export interface ISkillCapabilities {
  canWrite: boolean;
  canDelete: boolean;
}

// NOTE: NSAI-3309 — the sharing-aware capability set. It supersedes
// ISkillCapabilities for any surface that has to tell an owner from an Editor from
// a Viewer; `resolveSkillCapabilities` stays for the pre-sharing call sites that
// only need write/delete.
export interface IResolveSkillShareCapabilitiesProps {
  accessRole: ESkillAccessRole;
  isPublic: boolean;
  // NOTE: True while the skill is a copy still linked to an Organization Catalog
  // version, which makes its instructions and resources read-only.
  isLinkedToCatalog: boolean;
  canWriteVisibility: boolean;
  canDeleteVisibility: boolean;
  // NOTE: The caller holds private_skill_share:write.
  canShare: boolean;
}

export interface ISkillShareCapabilities {
  canEditContent: boolean;
  canChangeLifecycle: boolean;
  canChangeVisibility: boolean;
  canDelete: boolean;
  canManageShares: boolean;
  // NOTE: NSAI-6578 — the recipient's own two actions: removing themselves from
  // the share, and hiding the skill from their own automatic discovery. Both need
  // nothing beyond the share itself.
  canLeaveShare: boolean;
  canHideFromMyDiscovery: boolean;
  isOwner: boolean;
  isEditor: boolean;
  isViewer: boolean;
}

// NOTE: NSAI-3311 — one Organization Catalog entry's capabilities for this caller.
export interface IResolveCatalogEntryCapabilitiesProps {
  entryStatus: EOrganizationSkillStatus;
  latestVersionStatus: EOrganizationSkillStatus | null;
  isInstalled: boolean;
  isUpdateAvailable: boolean;
  // NOTE: The caller holds organization_skill:curate.
  canCurate: boolean;
  // NOTE: The caller holds organization_skill:read AND private_skill:write in the
  // target project — installing writes a private skill there.
  canInstall: boolean;
  // NOTE: NSAI-5839 — this project PUBLISHED the entry, so its source skill is
  // already here under that name and an install is guaranteed to 409. Reported by
  // the server on the entry; never computed by a client.
  isSourceInProject?: boolean;
  // NOTE: NSAI-5839 — another skill in this project already holds the entry's
  // name, so the install would 409 on the unique index. Also the server's answer.
  isNameTakenInProject?: boolean;
}

// NOTE: NSAI-5839 — WHY an install is not offered. A catalogue entry reported
// "Not installed" with no install control anywhere and no explanation, so a member
// could see the skill exists, read its metadata, and had no way to obtain it or to
// find out why. Every branch that turns `canInstall` off now names itself, so the
// UI can say the reason instead of rendering nothing.
//
// Ordered from most to least fundamental, so the reason a person sees is the one
// they would have to resolve first.
export enum ESkillInstallBlockReason {
  // NOTE: The role lacks organization_skill:read or the private-skill create
  // permissions. Nothing the person can do about it themselves — which is exactly
  // why saying so beats a blank space.
  //
  // This REVERSES a prior decision. Mobile's own resolver, which this rule
  // replaces, deliberately reported no message for a missing permission: "a
  // missing permission is not a state to explain per entry". NSAI-5839 asks for
  // the opposite, and is right to: the entry advertises "Not installed" either
  // way, so staying quiet leaves a person looking at a state they are told they
  // are not in, with nothing to act on. Naming it at least points at the admin
  // who can change it.
  NOT_PERMITTED = "not_permitted",
  // NOTE: Retired. New installs stop; copies already installed keep working.
  ENTRY_REMOVED = "entry_removed",
  // NOTE: Already installed in this project — the update path applies instead.
  ALREADY_INSTALLED = "already_installed",
  // NOTE: This project published it, so the original is already here.
  SOURCE_IN_PROJECT = "source_in_project",
  // NOTE: An unrelated skill of the same name is in the way; the install would
  // collide on the unique index.
  NAME_TAKEN = "name_taken",
}

export interface ISkillCatalogEntryCapabilities {
  canInstall: boolean;
  // NOTE: NSAI-5839 — null when `canInstall` is true. Non-null names the single
  // reason it is false, so a screen explains itself rather than showing an empty
  // space where a control would be.
  installBlockedBy: ESkillInstallBlockReason | null;
  canUpdate: boolean;
  canPublishVersion: boolean;
  canDeprecate: boolean;
  canRemove: boolean;
  isDeprecated: boolean;
  isRemoved: boolean;
}

export interface IResolveCatalogVersionCapabilitiesProps {
  // NOTE: The entry-level answer from resolveCatalogEntryCapabilities. It carries
  // the permissions and the entry's own state; only the version status below is
  // per-row.
  entryCapabilities: ISkillCatalogEntryCapabilities;
  versionStatus: EOrganizationSkillStatus;
  // NOTE: True for THE installed version, not merely "the entry is installed".
  isVersionInstalled: boolean;
}

export interface ISkillCatalogVersionCapabilities {
  canInstallVersion: boolean;
  canUpdateToVersion: boolean;
  canDeprecateVersion: boolean;
  canRemoveVersion: boolean;
  isVersionRemoved: boolean;
}

/* -------------------------------------------------------------------------
 * Import issues
 * ---------------------------------------------------------------------- */

export enum ESkillIssueSeverity {
  ERROR = "error",
  WARNING = "warning",
}

// NOTE: Which of a preview's FOUR lists an issue came from. Kept on the mapped
// issue so a view can group by origin, and so a reviewer can see at a glance
// that all four were read — an unreadable SKILL.md leaves `errors` empty and
// explains itself in `manifestErrors`, which is exactly the case a client that
// only checks `errors` gets wrong.
export enum ESkillIssueOrigin {
  BUNDLE = "bundle",
  MANIFEST = "manifest",
}

export type TSkillIssueCode =
  | ESkillBundleErrorCode
  | ESkillBundleWarningCode
  | ESkillManifestErrorCode;

// NOTE: One issue, flattened for rendering. `messageKey` is the server's
// fully-qualified i18n key and `args` its interpolation values, so a client
// translates rather than displaying server prose. `locator` is the resource
// path (bundle) or the frontmatter field (manifest) — kept as DATA, never
// interpolated into markup.
export interface ISkillImportIssue {
  code: TSkillIssueCode;
  messageKey: string;
  args: TSkillIssueArgs;
  severity: ESkillIssueSeverity;
  origin: ESkillIssueOrigin;
  locator: string | null;
}

export interface IFormatSkillSourceRevisionProps {
  sourceType: ESkillSourceType;
  sourceRevision: string | null;
}

export interface IMapBundleIssueProps {
  issue: IRawBundleIssue;
  severity: ESkillIssueSeverity;
}

export interface IMapManifestIssueProps {
  issue: IRawManifestIssue;
  severity: ESkillIssueSeverity;
}

export interface IImportStageTransitionProps {
  from: ESkillImportStage;
  to: ESkillImportStage;
}

export interface IMapBundlePreviewIssuesProps {
  errors: readonly IRawBundleIssue[];
  warnings: readonly IRawBundleIssue[];
  manifestErrors: readonly IRawManifestIssue[];
  manifestWarnings: readonly IRawManifestIssue[];
}

export interface IRawBundleIssue {
  code: ESkillBundleErrorCode | ESkillBundleWarningCode;
  messageKey: string;
  path: string | null;
  args: TSkillIssueArgs;
}

export interface IRawManifestIssue {
  code: ESkillManifestErrorCode;
  messageKey: string;
  field: string | null;
  args: TSkillIssueArgs;
}

export interface IMapPastePreviewIssuesProps {
  errors: readonly IRawManifestIssue[];
  warnings: readonly IRawManifestIssue[];
}

export interface ISkillImportIssueSummary {
  issues: ISkillImportIssue[];
  errors: ISkillImportIssue[];
  warnings: ISkillImportIssue[];
  hasErrors: boolean;
  hasWarnings: boolean;
}

/* -------------------------------------------------------------------------
 * Import state machine
 * ---------------------------------------------------------------------- */

export enum ESkillImportSource {
  PASTE = "paste",
  ZIP = "zip",
  GITHUB = "github",
}

// NOTE: The import flow is input → preview → confirmation → result, and the
// ONLY edge into COMMITTING comes from CONFIRMING. That is what makes "never
// import before a successful preview" a property of the state machine rather
// than a rule each screen has to remember.
export enum ESkillImportStage {
  INPUT = "input",
  UPLOADING = "uploading",
  PREVIEWING = "previewing",
  CONFIRMING = "confirming",
  COMMITTING = "committing",
  RESULT = "result",
  FAILED = "failed",
}

export enum ESkillImportTarget {
  NEW = "new",
  EXISTING = "existing",
}

export interface IResolveImportTargetProps {
  target: ESkillImportTarget;
  targetSkillUuid: string | null;
}

export interface ICanCommitImportProps {
  stage: ESkillImportStage;
  isPreviewValid: boolean;
  hasErrors: boolean;
  hasScripts: boolean;
  isScriptRiskAcknowledged: boolean;
  target: ESkillImportTarget;
  targetSkillUuid: string | null;
  // NOTE: NSAI-5805 — whether this import would DELETE files from the skill it
  // replaces. An update is a full replace with no undo, so a bundle that omits a
  // file removes it permanently; the preview used to be worded identically to a
  // create preview and said nothing about it.
  hasRemovedFiles: boolean;
  isFileRemovalAcknowledged: boolean;
}

export enum ESkillCommitBlockReason {
  WRONG_STAGE = "wrong_stage",
  PREVIEW_INVALID = "preview_invalid",
  HAS_ERRORS = "has_errors",
  SCRIPTS_NOT_ACKNOWLEDGED = "scripts_not_acknowledged",
  TARGET_MISSING = "target_missing",
  // NOTE: NSAI-5805 — the import would permanently delete files from the skill it
  // replaces, and nobody has said that is intended.
  FILE_REMOVAL_NOT_ACKNOWLEDGED = "file_removal_not_acknowledged",
}

export interface ISkillCommitDecision {
  canCommit: boolean;
  blockedBy: ESkillCommitBlockReason | null;
}

/* -------------------------------------------------------------------------
 * Safe errors and analytics
 * ---------------------------------------------------------------------- */

export interface IResolveMessageKeyProps {
  statusCode: number | null;
  isDeleteExpected: boolean;
  isReadExpected: boolean;
  fallbackMessageKey: string;
}

export interface IResolveSkillIssueMessageKeyProps {
  messageKey: string;
}

// NOTE: A 403 means three different things depending on what the caller was
// attempting, so the intent is passed in rather than guessed.
export interface IResolveForbiddenKeyProps {
  isDeleteExpected: boolean;
  isReadExpected: boolean;
}

export interface IResolveConflictMessageKeyProps {
  isNameConflictExpected: boolean;
}

export interface ISanitizeSkillErrorProps {
  error: unknown;
  fallbackMessageKey: string;
  // NOTE: A 403 and a 409 each cover two situations the server cannot tell
  // apart for the caller, so the caller says which one it was attempting.
  isDeleteExpected?: boolean;
  isReadExpected?: boolean;
  isNameConflictExpected?: boolean;
}

// NOTE: What a client may show. `messageKey` is always an i18n key this side
// owns — server prose never reaches a user through this path, because a server
// message can quote a path, a key or a URL that must not be rendered.
export interface ISafeSkillError {
  messageKey: string;
  args: TSkillIssueArgs;
  statusCode: number | null;
  retryAfterSeconds: number | null;
  isRetryable: boolean;
  // NOTE: True when the user cancelled. A caller must not raise an error toast
  // for it — the cancel WAS the intended outcome.
  isCancelled: boolean;
}

// NOTE: The actions a Skills client may report. An enum rather than a string so
// a caller cannot pass a server message as the event name.
export enum ESkillAnalyticsAction {
  LIST_VIEWED = "skill_list_viewed",
  DETAIL_VIEWED = "skill_detail_viewed",
  CREATED = "skill_created",
  UPDATED = "skill_updated",
  ENABLED = "skill_enabled",
  DISABLED = "skill_disabled",
  ARCHIVED = "skill_archived",
  RESTORED = "skill_restored",
  DELETED = "skill_deleted",
  IMPORT_PREVIEWED = "skill_import_previewed",
  IMPORTED = "skill_imported",
  IMPORT_FAILED = "skill_import_failed",
  RESOURCES_UPDATED = "skill_resources_updated",
}

export interface IBuildSkillAnalyticsEventProps {
  action: ESkillAnalyticsAction;
  skillUuid?: string | null;
  sourceType?: ESkillSourceType | null;
  // NOTE: A number, never a string. An HTTP status or a count is safe; a server
  // message is exactly the class of value this file exists to keep out.
  resultCode?: number | null;
  resourceCount?: number | null;
  hasScripts?: boolean | null;
}

// NOTE: The ONLY shape that may leave a client as telemetry. Ids, enum values
// and counts — never a name, a description, instructions, a metadata value, a
// resource path, file bytes, a repository credential, a signed URL or an S3 key.
export interface ISkillAnalyticsEvent {
  action: ESkillAnalyticsAction;
  skillUuid?: string;
  sourceType?: ESkillSourceType;
  resultCode?: number;
  resourceCount?: number;
  hasScripts?: boolean;
}
